import { Subscription } from 'rxjs';
import { ReportElement, ReportUnits, ReportPaperSize, ReportPageMargin, ElementType, BORDER_STYLE, FONT_FAMILY } from '../../../_objects/report-element';
import { ReportPreviewMetadata, UpdateMetadata, PropertyPreviewMetadata, FieldType } from '../../../_services/offline-report-preview.service';
import { ReportCommonSection } from './report-common-section';
import { TemplateCustomElements } from './report-template';

export class ReportCustomElement extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public fontSize: string;
  public fontFamily: string;
  public zIndex: number;
  public dynamicDataField: any;
  public itemDataSource: string;
  public itemWidth: string;
  public itemHeight: string;
  public borderWidth: string;
  public borderStyle: string;
  public borderColor: string;
  public itemMargin: string;
  public maxItemCount: number;
  public itemContainerHeight: string;
  public titleText: string;
  public titleFontSize: string;
  public titleAlign: string;
  public titleBackgroundColor: string;
  public designElementId: string;
  public designElementName: string;

  private units: ReportUnits;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  private baseDef: TemplateCustomElements;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaZIndex = 'zIndex';

  private readonly metaItemWidth = 'itemWidth';
  private readonly metaItemHeight = 'itemHeight';
  private readonly metaItemMargin = 'itemMargin';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaMaxItemCount = 'maxItemCount';
  private readonly metaCaptions = 'captionsList';
  private readonly metaItemContainerHeight = 'itemContainerHeight';
  private readonly metaTitleText = 'titleText';
  private readonly metaTitleFontSize = 'titleFontSize';
  private readonly metaTitleAlign = 'titleAlign';
  private readonly metaTitleBackgroundColor = 'titleBackgroundColor';

  private readonly metaItemDataSource = 'itemDataSource';
  private readonly metaDesignElementId = 'designElementId';
  private readonly metaDesignElementName = 'designElementName';

  constructor(  type: ElementType, index: number, units: ReportUnits, header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin, templateCustomElements?: TemplateCustomElements) {
    super(type, index, !(templateCustomElements == null), templateCustomElements ? templateCustomElements.id : null);

    this.units = units;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    if (templateCustomElements) {
      this.baseDef = templateCustomElements;
    } else {
      this.baseDef = <TemplateCustomElements>{
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: paperSize.getWidthNum(),
        height: paperSize.getHeightNum(),
        fontSize: 4,
        fontFamily: '',
        zIndex: 0,
        itemWidth: paperSize.getWidthNum() * 0.4,
        itemHeight: paperSize.getWidthNum() * 0.4,
        borderWidth: 0.25,
        borderStyle: 'solid',
        borderColor: '#ccc',
        itemMargin: 1,
        maxItemCount: 2,
        itemDataSource: '',
        dynamicDataField: '',
        itemContainerHeight: paperSize.getHeightNum() * 0.8,
        titleText: 'Custom Elements',
        titleAlign: 'Left',
        titleBackgroundColor: 'transparent',
        titleFontSize: 6,
        designElementId: '',
        designElementName: ''
      };
    }

    this.top = this.units.concatenate('sizeUnit', this.baseDef[this.metaTop]);
    this.left = this.units.concatenate('sizeUnit', this.baseDef[this.metaLeft]);

    this.width = this.units.concatenate('sizeUnit', this.baseDef[this.metaWidth]);
    this.height = this.units.concatenate('sizeUnit', this.baseDef[this.metaHeight]);

    this.fontSize = this.units.concatenate('sizeUnit', this.baseDef[this.metaFontSize]);
    this.fontFamily = this.baseDef[this.metaFontFamily];

    this.itemWidth = this.units.concatenate('sizeUnit', this.baseDef[this.metaItemWidth]);
    this.itemHeight = this.units.concatenate('sizeUnit', this.baseDef[this.metaItemHeight]);
    this.itemMargin = this.units.concatenate('sizeUnit', this.baseDef[this.metaItemMargin]);
    this.borderWidth = this.units.concatenate('sizeUnit', this.baseDef[this.metaBorderWidth]);
    this.borderStyle = this.baseDef[this.metaBorderStyle];
    this.borderColor = this.baseDef[this.metaBorderColor];
    this.maxItemCount = this.baseDef[this.metaMaxItemCount];
    this.itemContainerHeight = this.units.concatenate('sizeUnit', this.baseDef[this.metaItemContainerHeight]);

    this.titleText = this.baseDef[this.metaTitleText];
    this.titleFontSize = this.units.concatenate('sizeUnit', this.baseDef[this.metaTitleFontSize]);
    this.titleBackgroundColor = this.baseDef[this.metaTitleBackgroundColor];
    this.titleAlign = this.baseDef[this.metaTitleAlign];

    this.itemDataSource = this.baseDef[this.metaItemDataSource];

    this.zIndex = this.baseDef[this.metaZIndex];
    this.designElementId = this.baseDef[this.metaDesignElementId];
    this.designElementName = this.baseDef[this.metaDesignElementName];
    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    const width = this.baseDef[this.metaWidth]
          - this.pageMargin.getRightNum()
          - this.pageMargin.getLeftNum();
    const height = this.baseDef[this.metaHeight]
          - this.pageMargin.getTopNum()
          - this.pageMargin.getBottomNum()
          - this.header.getHeightNum()
          - this.footer.getHeightNum();
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: width,
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaItemContainerHeight,
      value: height * 0.8,
      oldValue: this.baseDef[this.metaItemContainerHeight]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: height,
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }

  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }

  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }

  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.fontSize = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.name] = metadata.value as string;
        this.fontFamily = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaItemDataSource:
        this.baseDef[metadata.name] = metadata.value as string;
        this.itemDataSource = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.borderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaItemContainerHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.itemContainerHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleText:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleText = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.titleFontSize = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleBackgroundColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleBackgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTitleAlign:
        this.baseDef[metadata.name] = metadata.value as string;
        this.titleAlign = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDesignElementId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.designElementId = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaDesignElementName:
        this.baseDef[metadata.name] = metadata.value as string;
        this.designElementName = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) { }

  public getTemplateJSON(): TemplateCustomElements {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaTitleAlign, parentName: null, value: this.titleAlign,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'TextBox'
      },
      {
        name: this.metaBorderWidth, parentName: null, value: this.borderWidth,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaBorderColor, parentName: null, value: this.borderColor,
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: this.borderStyle,
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: true,
        isEdited: false,
        group: 'Line'
      },
      {
        name: this.metaTitleBackgroundColor, parentName: null, value: this.titleBackgroundColor,
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Background'
      },
      {
        name: this.metaFontFamily, parentName: null, value: this.fontFamily,
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: true,
        isEdited: false,
        group: 'Font'
      },
      {
        name: this.metaFontSize, parentName: null, value: this.fontSize,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Font'
      },
      {
        name: this.metaTitleFontSize, parentName: null, value: this.titleFontSize,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: 'Font'
      },
      {
        name: this.metaTitleText, parentName: null, value: this.titleText,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: true,
        isEdited: false,
      }
    ];
  }
}
